import type { HierarchyResult, HierarchyNode } from '../../types';

/**
 * Transforms a HierarchyResult object into a text-based representation
 * compatible with the HierarchyDiagram component.
 */

export type ChartOrientation = 'north' | 'south' | 'east' | 'west';
class HierarchyResultTransformer {
  private readonly hierarchy: HierarchyResult;
  private readonly rootNode: HierarchyNode;
  private readonly orientation: ChartOrientation;

  constructor(hierarchy: HierarchyResult, orientation: ChartOrientation) {
    this.hierarchy = hierarchy;
    this.rootNode = hierarchy.nodes[hierarchy.originalRecordId];
    this.orientation = orientation;
  }

  /**
   * Generates the full diagram text for the entire hierarchy.
   * @returns A string compatible with HierarchyDiagram.
   */
  public toDiagramText(): string {
    const visited = new Set<string>();

    const hierarchyText = this.buildNodeText(this.rootNode, 0, visited);

    // Add the diagram type and orientation at the beginning
    return `hierarchy {orientation: ${this.orientation}}\n${hierarchyText}`;
  }

  /**
   * Recursively builds the text for a node and its descendants.
   * @param node The node to process.
   * @param indentLevel The current indentation level.
   * @param visited A set of visited node IDs to prevent cycles.
   * @param hierarchySource The hierarchy to use for node lookup (defaults to this.hierarchy).
   * @returns A string representing the node and its subtree.
   */
  private buildNodeText(
    node: HierarchyNode,
    indentLevel: number,
    visited: Set<string>,
    hierarchySource: HierarchyResult = this.hierarchy
  ): string {
    // 1. Cycle Detection
    if (visited.has(node.record.recordId)) {
      return ''; // Stop if we've already processed this node
    }
    visited.add(node.record.recordId);

    // 2. Indentation
    const indent = '  '.repeat(indentLevel);

    // 3. Node Label
    const nodeLine = `${indent}${node.record.recordId}`;

    // 4. Recursive Call for Children
    const childLines = node.childIds
      .map(childId => hierarchySource.nodes[childId])
      .filter(Boolean) // Filter out any missing child nodes
      .map(childNode =>
        this.buildNodeText(childNode, indentLevel + 1, visited, hierarchySource)
      )
      .filter(Boolean) // Filter out empty strings from cycles
      .join('\n');

    // 5. Output
    let output = nodeLine;
    if (childLines) {
      output += `\n${childLines}`;
    }

    return output;
  }
}

export default HierarchyResultTransformer;
